import { isSubscriptionTariff, isTrialTariff } from './payments';

export const AnalyticsApp = {
    focuslee: 'focuslee',
    healthlee: 'healthlee',
} as const;

export type AnalyticsAppId = (typeof AnalyticsApp)[keyof typeof AnalyticsApp];

export const AnalyticsPlatform = {
    telegramMiniApp: 'telegram_mini_app',
    telegramBot: 'telegram_bot',
} as const;

export type AnalyticsPlatformId = (typeof AnalyticsPlatform)[keyof typeof AnalyticsPlatform];

export const PremiumStatus = {
    none: 'none',
    trial: 'trial',
    active: 'active',
    lapsed: 'lapsed',
} as const;

export type PremiumStatusId = (typeof PremiumStatus)[keyof typeof PremiumStatus];

export const AnalyticsEvent = {
    pageViewed: '[Amplitude] Page Viewed',
    miniAppOpened: 'Mini App Opened',
    onboardingStepCompleted: 'Onboarding Step Completed',
    onboardingCompleted: 'Onboarding Completed',
    voiceCommandStarted: 'Voice Command Started',
    voiceCommandSucceeded: 'Voice Command Succeeded',
    voiceCommandFailed: 'Voice Command Failed',
    paywallViewed: 'Paywall Viewed',
    checkoutStarted: 'Checkout Started',
    trialStarted: 'Trial Started',
    paymentSucceeded: 'Payment Succeeded',
    paymentFailed: 'Payment Failed',
    paymentRefunded: 'Payment Refunded',
    subscriptionRenewed: 'Subscription Renewed',
    subscriptionCancelled: 'Subscription Cancelled',
    subscriptionRenewalMissed: 'Subscription Renewal Missed',
    subscriptionRenewalReminderSent: 'Subscription Renewal Reminder Sent',
    premiumExpired: 'Premium Expired',
    crossSellShown: 'Cross Sell Shown',
    crossSellClicked: 'Cross Sell Clicked',
    foodLogged: 'Food Logged',
    waterLogged: 'Water Logged',
    stepsLogged: 'Steps Logged',
    weightLogged: 'Weight Logged',
    trainingLogged: 'Training Logged',
    photoFoodLogAttempted: 'Photo Food Log Attempted',
    textFoodLogAttempted: 'Text Food Log Attempted',
    voiceFoodLogAttempted: 'Voice Food Log Attempted',
    barcodeScanned: 'Barcode Scanned',
    referralOpened: 'Referral Opened',
    questAbandoned: 'Quest Abandoned',
} as const;

export type FoodLogMethod = 'manual' | 'restaurant' | 'photo_ai' | 'gallery_ai' | 'text_ai' | 'voice_ai' | 'barcode';

export type FoodPhotoSource = 'camera' | 'gallery';

const PREMIUM_FOOD_LOG_METHODS = new Set<FoodLogMethod>(['photo_ai', 'gallery_ai', 'text_ai', 'voice_ai']);

export function foodLogIsPremiumFeature(method: FoodLogMethod): boolean {
    return PREMIUM_FOOD_LOG_METHODS.has(method);
}

export type StartParamAnalytics = {
    cohort?: string;
    invite_code?: string;
    referrer_user_id?: string;
};

export function resolvePremiumStatus(role?: string | null, tariff?: string | null): PremiumStatusId {
    if (role === 'Premium' || role === 'Admin') {
        if (isTrialTariff(tariff)) return PremiumStatus.trial;
        if (isSubscriptionTariff(tariff)) return PremiumStatus.active;
        return PremiumStatus.active;
    }
    if (tariff) return PremiumStatus.lapsed;
    return PremiumStatus.none;
}

export function parseStartParamAnalytics(startParam?: string | null): StartParamAnalytics {
    if (!startParam) return {};
    const cohort = startParam.match(/(?:^|[_\-])cohort[_-]([^_\-]+)/i);
    const invite = startParam.match(/(?:^|[_\-])invite[_-]([^_\-]+)/i);
    const referrer = startParam.match(/(?:^|[_\-])ref[_-](\d+)/i);
    return {
        ...(cohort?.[1] ? { cohort: cohort[1] } : {}),
        ...(invite?.[1] ? { invite_code: invite[1] } : {}),
        ...(referrer?.[1] ? { referrer_user_id: referrer[1] } : {}),
    };
}

export function analyticsAppForProductKey(productKey: string): AnalyticsAppId {
    if (productKey.startsWith('healthlee_') || productKey.startsWith('health_')) {
        return AnalyticsApp.healthlee;
    }
    return AnalyticsApp.focuslee;
}
